(() => {
  // Shared behavior for the Cold Air Lab sections, including mobile controls.
  const playVideo = (video) => {
    if (!video || typeof video.play !== 'function') return;
    video.play().catch(() => {});
  };

  const pauseVideo = (video) => {
    if (video && typeof video.pause === 'function') video.pause();
  };

  const playMedia = (media) => {
    if (!media) return;
    if (media.tagName !== 'VIDEO-MEDIA') {
      playVideo(media);
      return;
    }

    // `ca.js` is intentionally loaded before the theme bundle. During that
    // brief interval a <video-media> element has not yet been upgraded, so it
    // does not expose the theme's play() method. Wait for the upgrade instead
    // of treating the custom element itself as a native <video>.
    if (typeof media.play === 'function') {
      media.play();
      return;
    }
    playVideo(media.querySelector('video'));
    customElements.whenDefined('video-media').then(() => {
      if (media.isConnected && typeof media.play === 'function') media.play();
    });
  };

  const pauseMedia = (media) => {
    if (!media) return;
    if (media.tagName !== 'VIDEO-MEDIA') {
      pauseVideo(media);
      return;
    }
    if (typeof media.pause === 'function') {
      media.pause();
      return;
    }
    // The un-upgraded component has no player to pause yet. Its template is
    // inert, but a native video can exist during a section-editor rerender.
    pauseVideo(media.querySelector('video'));
  };

  const createDeferredMediaPlayer = (media) => {
    if (!media || media.tagName !== 'VIDEO-MEDIA' || media.querySelector('video, iframe')) return;
    const create = () => {
      if (!media.isConnected || media.querySelector('video, iframe')) return;
      if (typeof media.playerTarget === 'function') void media.player;
    };
    if (customElements.get('video-media')) create();
    else customElements.whenDefined('video-media').then(create);
  };

  const warmPosterImages = (media, eager = false) => {
    if (!media) return;
    const mobilePoster = media.querySelector('.cal-media__poster--mobile');
    const useMobilePoster = Boolean(mobilePoster && matchMedia('(max-width: 680px)').matches);
    media.querySelectorAll('.cal-media__poster').forEach((poster) => {
      // A mobile video can include a desktop poster underneath its mobile
      // poster as a visual fallback. Only warm the file that can actually be
      // seen at this breakpoint; otherwise every mobile video starts two
      // image requests before it is even close to view.
      if (useMobilePoster && poster !== mobilePoster) return;
      poster.loading = 'eager';
      poster.setAttribute('loading', 'eager');
      if (eager) {
        poster.setAttribute('fetchpriority', 'high');
        if ('fetchPriority' in poster) poster.fetchPriority = 'high';
      }
      if (poster.dataset.calPosterWarmed === 'true') return;
      poster.dataset.calPosterWarmed = 'true';
      const image = new Image();
      image.decoding = 'async';
      const sourceSet = poster.getAttribute('srcset');
      const sizes = poster.getAttribute('sizes');
      if (sourceSet) image.srcset = sourceSet;
      if (sizes) image.sizes = sizes;
      const source = poster.currentSrc || poster.src;
      if (!source) return;
      image.src = source;
    });
  };

  const initHeroCounters = (section) => {
    if (section.dataset.calHeroCountersReady === 'true') return;
    const counters = [...section.querySelectorAll('[data-cal-hero-counter]')];
    if (!counters.length) return;
    section.dataset.calHeroCountersReady = 'true';

    const counterStates = counters.map((counter) => {
      const source = counter.dataset.calHeroCounter || counter.textContent.trim();
      const match = source.match(/^(.*?)(\d[\d,]*(?:\.\d+)?)([^\d]*)$/);
      if (!match) return null;
      const numericText = match[2].replace(/,/g, '');
      const target = Number(numericText);
      if (!Number.isFinite(target)) return null;
      const decimals = (numericText.split('.')[1] || '').length;
      return {
        counter,
        prefix: match[1],
        suffix: match[3],
        target,
        decimals,
        comma: match[2].includes(',')
      };
    }).filter(Boolean);
    if (!counterStates.length) return;

    const format = (value, state) => {
      const number = state.decimals ? value.toFixed(state.decimals) : String(Math.round(value));
      const formatted = state.comma
        ? Number(number).toLocaleString('en-US', { minimumFractionDigits: state.decimals, maximumFractionDigits: state.decimals })
        : number;
      return `${state.prefix}${formatted}${state.suffix}`;
    };
    const render = (progress) => counterStates.forEach((state) => {
      state.counter.textContent = format(state.target * progress, state);
    });
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      render(1);
      return;
    }

    // Keep the reference timing intent, but do not rely only on the first
    // wheel/touch event. In Shopify, the section can already be visible before
    // this script binds those listeners, leaving the counters stuck at 0.
    render(0);
    let started = false;
    let observer;
    const triggerElement = section.querySelector('.cal-hero__stats') || section;
    const isTriggerVisible = () => {
      const rect = triggerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < viewportHeight * .96 && rect.bottom > viewportHeight * .04;
    };
    const cleanup = () => {
      observer?.disconnect();
      window.removeEventListener('wheel', start);
      window.removeEventListener('touchmove', start);
      window.removeEventListener('scroll', startIfVisible);
      window.removeEventListener('pageshow', startIfVisible);
      window.removeEventListener('load', startIfVisible);
    };
    const start = () => {
      if (started) return;
      started = true;
      cleanup();
      const duration = 1400;
      let startTime;
      const tick = (now) => {
        if (startTime === undefined) startTime = now;
        const progress = Math.min((now - startTime) / duration, 1);
        render(1 - Math.pow(1 - progress, 3));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const startIfVisible = () => {
      if (isTriggerVisible()) start();
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
      observer.observe(triggerElement);
    }
    window.addEventListener('wheel', start, { passive: true });
    window.addEventListener('touchmove', start, { passive: true });
    window.addEventListener('scroll', startIfVisible, { passive: true });
    window.addEventListener('pageshow', startIfVisible);
    window.addEventListener('load', startIfVisible);
    requestAnimationFrame(startIfVisible);
    window.setTimeout(startIfVisible, 350);
  };

  const formatHeroTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const wholeSeconds = Math.floor(seconds);
    const minutes = Math.floor(wholeSeconds / 60);
    const remainder = String(wholeSeconds % 60).padStart(2, '0');
    return `${minutes}:${remainder}`;
  };

  const initHeroCarousel = (section) => {
    const carousel = section.querySelector('[data-cal-hero-carousel]');
    if (!carousel || carousel.dataset.calHeroCarouselReady === 'true') return;

    const track = carousel.querySelector('[data-cal-hero-media-track]');
    const slides = [...carousel.querySelectorAll('[data-cal-hero-slide]')];
    const tabs = [...carousel.querySelectorAll('[data-cal-hero-tab]')];
    const progress = carousel.querySelector('[data-cal-hero-progress]');
    const progressInput = carousel.querySelector('[data-cal-hero-progress-input]');
    const progressFill = carousel.querySelector('[data-cal-hero-progress-fill]');
    const progressThumb = carousel.querySelector('[data-cal-hero-progress-thumb]');
    const playButton = carousel.querySelector('[data-cal-hero-play]');
    if (!track || !slides.length || !progress || !progressInput || !playButton) return;

    carousel.dataset.calHeroCarouselReady = 'true';
    track.style.setProperty('--cal-hero-slide-count', String(slides.length));
    let activeIndex = 0;
    let isPlaying = carousel.dataset.calHeroAutoplay === 'true';
    let isScrubbing = false;
    let wasPlayingBeforeScrub = false;
    let swipeStartX = 0;
    let swipeDeltaX = 0;
    let isSwiping = false;

    const getMedia = (slide) => slide?.querySelector('video-media, video');
    const getVideo = (media) => {
      if (!media) return null;
      return media.tagName === 'VIDEO' ? media : media.querySelector('video');
    };
    const bindVideo = (video) => {
      if (!video || video.dataset.calHeroCarouselVideoReady === 'true') return;
      video.dataset.calHeroCarouselVideoReady = 'true';
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('loop', '');
      ['loadedmetadata', 'durationchange', 'timeupdate', 'progress', 'canplay'].forEach((eventName) => {
        video.addEventListener(eventName, updateProgress);
      });
      video.addEventListener('play', () => {
        if (video === getVideo(getMedia(slides[activeIndex]))) {
          isPlaying = true;
          updatePlayButton();
        }
      });
      video.addEventListener('pause', () => {
        if (video === getVideo(getMedia(slides[activeIndex]))) {
          isPlaying = false;
          updatePlayButton();
        }
      });
      video.addEventListener('ended', () => {
        if (video === getVideo(getMedia(slides[activeIndex]))) {
          isPlaying = false;
          updatePlayButton();
        }
      });
    };
    const requestVideo = (media, callback) => {
      const existingVideo = getVideo(media);
      if (existingVideo) {
        bindVideo(existingVideo);
        callback(existingVideo);
        return;
      }
      if (!media || media.tagName !== 'VIDEO-MEDIA') return;

      let observer;
      const finish = () => {
        const video = getVideo(media);
        if (!video) return;
        observer?.disconnect();
        bindVideo(video);
        callback(video);
      };
      observer = new MutationObserver(finish);
      observer.observe(media, { childList: true, subtree: true });
      const create = () => {
        if (!media.isConnected || getVideo(media)) return finish();
        try { void media.player; } catch {}
        finish();
      };
      if (customElements.get('video-media')) create();
      else customElements.whenDefined('video-media').then(create);
    };
    const getActiveVideo = () => getVideo(getMedia(slides[activeIndex]));
    const updateProgress = () => {
      const video = getActiveVideo();
      const duration = video?.duration;
      const ratio = video && Number.isFinite(duration) && duration > 0
        ? Math.min(1, Math.max(0, video.currentTime / duration))
        : 0;
      if (progressFill) progressFill.style.width = `${ratio * 100}%`;
      if (progressThumb) progressThumb.style.left = `${ratio * 100}%`;
      progressInput.value = String(ratio * 100);
      progressInput.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      progressInput.setAttribute('aria-valuetext', `${formatHeroTime(video?.currentTime || 0)} / ${formatHeroTime(duration || 0)}`);
    };
    const updatePlayButton = () => {
      const video = getActiveVideo();
      isPlaying = Boolean(video && !video.paused && !video.ended);
      playButton.classList.toggle('is-playing', isPlaying);
      playButton.setAttribute('aria-pressed', String(isPlaying));
      playButton.setAttribute('aria-label', isPlaying
        ? playButton.dataset.calPauseLabel || 'Pause video'
        : playButton.dataset.calPlayLabel || 'Play video');
    };
    const pauseVideoForSlide = (slide) => {
      const media = getMedia(slide);
      const video = getVideo(media);
      if (video) pauseVideo(video);
      if (media?.tagName === 'VIDEO-MEDIA') media.paused = true;
    };
    const playActiveVideo = () => {
      const media = getMedia(slides[activeIndex]);
      requestVideo(media, (video) => {
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');
        playVideo(video);
      });
      updatePlayButton();
    };
    const setProgressFromRatio = (ratio) => {
      const video = getActiveVideo();
      if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const nextRatio = Math.min(1, Math.max(0, ratio));
      video.currentTime = nextRatio * video.duration;
      if (progressFill) progressFill.style.width = `${nextRatio * 100}%`;
      if (progressThumb) progressThumb.style.left = `${nextRatio * 100}%`;
      progressInput.value = String(nextRatio * 100);
      progressInput.setAttribute('aria-valuenow', String(Math.round(nextRatio * 100)));
      progressInput.setAttribute('aria-valuetext', `${formatHeroTime(video.currentTime)} / ${formatHeroTime(video.duration)}`);
    };
    const setProgressFromPointer = (clientX) => {
      const rect = progress.getBoundingClientRect();
      setProgressFromRatio((clientX - rect.left) / rect.width);
    };
    const setActive = (index, shouldPlay = isPlaying, focus = false) => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      if (nextIndex !== activeIndex) pauseVideoForSlide(slides[activeIndex]);
      activeIndex = nextIndex;
      track.style.transform = `translate3d(-${activeIndex * (100 / slides.length)}%, 0, 0)`;
      slides.forEach((slide, currentIndex) => {
        const active = currentIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      tabs.forEach((tab, currentIndex) => {
        const active = currentIndex === activeIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        if (active && focus) tab.focus();
      });
      requestVideo(getMedia(slides[activeIndex]), (video) => {
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');
        if (shouldPlay) playVideo(video);
        else pauseVideo(video);
        updatePlayButton();
        updateProgress();
      });
      updatePlayButton();
      updateProgress();
    };
    const endScrub = () => {
      if (!isScrubbing) return;
      isScrubbing = false;
      if (wasPlayingBeforeScrub) playActiveVideo();
      progress.removeAttribute('data-dragging');
    };
    const endSwipe = () => {
      if (!isSwiping) return;
      isSwiping = false;
      track.style.transition = '';
      if (Math.abs(swipeDeltaX) > 48) setActive(activeIndex + (swipeDeltaX < 0 ? 1 : -1));
      else setActive(activeIndex);
      swipeDeltaX = 0;
    };

    progress.addEventListener('pointerdown', (event) => {
      if (event.target === progressInput) return;
      if (!getActiveVideo()) return;
      isScrubbing = true;
      wasPlayingBeforeScrub = isPlaying;
      progress.dataset.dragging = 'true';
      progress.setPointerCapture?.(event.pointerId);
      setProgressFromPointer(event.clientX);
      event.preventDefault();
    });
    progress.addEventListener('pointermove', (event) => {
      if (event.target !== progressInput && isScrubbing) setProgressFromPointer(event.clientX);
    });
    progress.addEventListener('pointerup', endScrub);
    progress.addEventListener('pointercancel', endScrub);
    progress.addEventListener('lostpointercapture', endScrub);
    progressInput.addEventListener('pointerdown', () => {
      if (!getActiveVideo()) return;
      isScrubbing = true;
      wasPlayingBeforeScrub = isPlaying;
      if (wasPlayingBeforeScrub) pauseVideo(getActiveVideo());
      progress.dataset.dragging = 'true';
    });
    progressInput.addEventListener('pointerup', endScrub);
    progressInput.addEventListener('pointercancel', endScrub);
    progressInput.addEventListener('input', () => {
      setProgressFromRatio(Number(progressInput.value) / 100);
    });
    progressInput.addEventListener('keydown', (event) => {
      const currentValue = Number(progressInput.value);
      const step = Number(progressInput.step) || 1;
      let nextValue;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextValue = currentValue + step;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextValue = currentValue - step;
      else if (event.key === 'Home') nextValue = 0;
      else if (event.key === 'End') nextValue = 100;
      else return;
      event.preventDefault();
      progressInput.value = String(Math.min(100, Math.max(0, nextValue)));
      setProgressFromRatio(Number(progressInput.value) / 100);
    });

    tabs.forEach((tab, index) => tab.addEventListener('click', () => setActive(index, isPlaying)));
    playButton.dataset.calPlayLabel = playButton.dataset.calPlayLabel || 'Play video';
    playButton.dataset.calPauseLabel = playButton.dataset.calPauseLabel || 'Pause video';
    playButton.addEventListener('click', () => {
      const video = getActiveVideo();
      if (!video) {
        playActiveVideo();
        return;
      }
      if (video.paused || video.ended) {
        isPlaying = true;
        playActiveVideo();
      } else {
        isPlaying = false;
        pauseVideo(video);
        updatePlayButton();
      }
    });

    carousel.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-cal-hero-controls]')) return;
      isSwiping = true;
      swipeStartX = event.clientX;
      carousel.setPointerCapture?.(event.pointerId);
      track.style.transition = 'none';
    });
    carousel.addEventListener('pointermove', (event) => {
      if (!isSwiping) return;
      swipeDeltaX = event.clientX - swipeStartX;
      const dragPercent = (swipeDeltaX / carousel.getBoundingClientRect().width) * (100 / slides.length);
      track.style.transform = `translate3d(calc(-${activeIndex * (100 / slides.length)}% + ${dragPercent}%), 0, 0)`;
    });
    carousel.addEventListener('pointerup', endSwipe);
    carousel.addEventListener('pointercancel', endSwipe);
    carousel.addEventListener('lostpointercapture', endSwipe);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const wasPlaying = Boolean(getActiveVideo() && !getActiveVideo().paused);
        pauseVideoForSlide(slides[activeIndex]);
        isPlaying = wasPlaying;
      } else if (isPlaying) {
        playActiveVideo();
      }
    });

    setActive(0, isPlaying);
  };

  const initHeroMedia = () => {
    document.querySelectorAll('video-media.cal-media[data-cal-hero-media]').forEach((media) => {
      if (media.dataset.calHeroMediaReady === 'true') return;
      media.dataset.calHeroMediaReady = 'true';

      const prepareVideo = () => {
        const video = media.querySelector('video');
        if (!video) return false;
        const carousel = media.closest('[data-cal-hero-carousel]');
        const shouldAutoplay = !carousel || carousel.dataset.calHeroAutoplay !== 'false';
        if (video.dataset.calHeroVideoReady === 'true') {
          if (shouldAutoplay) video.play().catch(() => {});
          return true;
        }
        video.dataset.calHeroVideoReady = 'true';
        video.autoplay = shouldAutoplay;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'auto';
        if (shouldAutoplay) video.setAttribute('autoplay', '');
        else video.removeAttribute('autoplay');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('loop', '');
        video.setAttribute('preload', 'auto');
        video.addEventListener('playing', () => {
          media.setAttribute('data-cal-video-ready', 'true');
        }, { once: true });
        try { video.load(); } catch {}
        if (shouldAutoplay) video.play().catch(() => {});
        return true;
      };
      const start = () => {
        if (document.visibilityState === 'hidden') return;
        const carousel = media.closest('[data-cal-hero-carousel]');
        if (carousel && carousel.dataset.calHeroAutoplay === 'false') return;
        media.paused = false;
        createDeferredMediaPlayer(media);
        playMedia(media);
        prepareVideo();
      };

      if (!prepareVideo()) {
        const videoObserver = new MutationObserver(() => {
          if (prepareVideo()) videoObserver.disconnect();
        });
        videoObserver.observe(media, { childList: true, subtree: true });
      }

      start();
      requestAnimationFrame(start);
      window.setTimeout(start, 120);
      window.setTimeout(start, 520);
      window.addEventListener('pageshow', start);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') start();
        else pauseMedia(media);
      });
    });
  };

  const initStory = (section) => {
    if (section.dataset.calStoryReady === 'true') return;
    const scrollArea = section.querySelector('[data-cal-story-scroll]');
    const layout = section.querySelector('.cal-story__layout');
    const steps = [...section.querySelectorAll('[data-cal-story-step]')];
    const panels = [...section.querySelectorAll('[data-cal-story-panel]')];
    const position = section.querySelector('[data-cal-story-position]');
    if (!steps.length || !panels.length) return;
    section.dataset.calStoryReady = 'true';
    let activeIndex = 0;
    let scrollFrame;

    const getPanelMedia = (panel) => panel?.querySelector('video-media') || panel?.querySelector('video');
    const getNativeVideo = (media) => {
      if (!media) return null;
      return media.tagName === 'VIDEO' ? media : media.querySelector('video');
    };
    const prepareStoryVideo = (video, preload = 'metadata', active = false) => {
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('loop', '');

      if (active) {
        video.autoplay = true;
        video.setAttribute('autoplay', '');
      } else {
        video.autoplay = false;
        video.removeAttribute('autoplay');
      }

      if (video.preload !== preload) {
        video.preload = preload;
        video.setAttribute('preload', preload);
      }
      if (preload !== 'none' && video.readyState < HTMLMediaElement.HAVE_METADATA && video.paused) {
        try { video.load(); } catch {}
      }
    };
    const bindStoryVideo = (panel, video, preload = 'metadata') => {
      const shouldPlay = panels[activeIndex] === panel && section.dataset.calStoryVisible === 'true';
      prepareStoryVideo(video, shouldPlay ? 'auto' : preload, shouldPlay);

      if (video.dataset.calStoryVideoReady === 'true') return;
      video.dataset.calStoryVideoReady = 'true';
      const requestActivePlayback = () => {
        if (document.visibilityState === 'hidden') return;
        if (panels[activeIndex] !== panel || section.dataset.calStoryVisible !== 'true') return;
        prepareStoryVideo(video, 'auto', true);
        playVideo(video);
      };
      video.addEventListener('loadeddata', requestActivePlayback);
      video.addEventListener('canplay', requestActivePlayback);
      video.addEventListener('playing', () => video.closest('video-media')?.setAttribute('data-cal-video-ready', 'true'));
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) requestActivePlayback();
    };
    const whenNativeVideo = (media, callback) => {
      const video = getNativeVideo(media);
      if (video) {
        callback(video);
        return;
      }
      if (!media || media.tagName !== 'VIDEO-MEDIA') return;

      const videoObserver = new MutationObserver(() => {
        const createdVideo = getNativeVideo(media);
        if (!createdVideo) return;
        videoObserver.disconnect();
        callback(createdVideo);
      });
      videoObserver.observe(media, { childList: true, subtree: true });
    };
    const createDeferredVideo = (media) => {
      if (!media || media.tagName !== 'VIDEO-MEDIA' || getNativeVideo(media)) return;
      const createVideo = () => {
        if (!media.isConnected || getNativeVideo(media)) return;
        if (typeof media.playerTarget === 'function') void media.player;
      };
      if (customElements.get('video-media')) createVideo();
      else customElements.whenDefined('video-media').then(createVideo);
    };
    const warmPanelMedia = (panel, eager = false) => {
      const media = getPanelMedia(panel);
      if (!media) return;
      const active = panels[activeIndex] === panel && section.dataset.calStoryVisible === 'true';
      const preload = eager || active ? 'auto' : 'metadata';
      if (media.tagName === 'VIDEO-MEDIA') {
        media.paused = !active;
        whenNativeVideo(media, (video) => {
          const freshActive = panels[activeIndex] === panel && section.dataset.calStoryVisible === 'true';
          bindStoryVideo(panel, video, freshActive ? 'auto' : preload);
          if (!freshActive) pauseVideo(video);
        });
        createDeferredVideo(media);
        return;
      }
      bindStoryVideo(panel, media, preload);
      if (!active) pauseVideo(media);
    };
    const warmNearbyPanels = () => {
      warmPanelMedia(panels[activeIndex], true);
      warmPanelMedia(panels[activeIndex + 1], true);
      warmPanelMedia(panels[activeIndex - 1], false);
    };
    const startPanelMedia = (panel) => {
      if (section.dataset.calStoryVisible !== 'true') return;
      const media = getPanelMedia(panel);
      if (!media) return;
      if (media.tagName === 'VIDEO-MEDIA') media.paused = false;
      warmPanelMedia(panel, true);
      playMedia(media);
      whenNativeVideo(media, (video) => {
        bindStoryVideo(panel, video, 'auto');
        if (panels[activeIndex] === panel && section.dataset.calStoryVisible === 'true') playVideo(video);
      });
    };
    const pausePanelMedia = (panel) => {
      const media = getPanelMedia(panel);
      if (!media) return;
      if (media.tagName === 'VIDEO-MEDIA') media.paused = true;
      pauseMedia(media);
      pauseVideo(getNativeVideo(media));
    };

    const activate = (index, focus = false) => {
      const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
      if (nextIndex === activeIndex && section.dataset.calStoryActivated === 'true') return;
      activeIndex = nextIndex;
      section.dataset.calStoryActivated = 'true';
      steps.forEach((step, current) => {
        const active = current === activeIndex;
        step.classList.toggle('is-active', active);
        step.classList.toggle('is-before', current < activeIndex);
        step.classList.toggle('is-after', current > activeIndex);
        step.setAttribute('aria-selected', String(active));
        step.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel, current) => {
        const active = current === activeIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        if (active) startPanelMedia(panel);
        else pausePanelMedia(panel);
      });
      if (position) position.dataset.calStoryPosition = `${String(activeIndex + 1).padStart(2, '0')} / ${String(steps.length).padStart(2, '0')}`;
      if (section.dataset.calStoryWarm === 'true' || section.dataset.calStoryVisible === 'true') warmNearbyPanels();
      if (focus) steps[activeIndex].focus();
    };

    const updateFromScroll = () => {
      scrollFrame = undefined;
      if (!scrollArea || !layout) return;
      const rect = scrollArea.getBoundingClientRect();
      const stickyTop = Number.parseFloat(getComputedStyle(layout).top) || 0;
      const travel = Math.max(scrollArea.offsetHeight - layout.offsetHeight, 1);
      const progressed = Math.max(0, Math.min(1, (stickyTop - rect.top) / travel));
      activate(Math.min(steps.length - 1, Math.floor(progressed * steps.length)));
    };

    const onScroll = () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateFromScroll);
    };

    steps.forEach((step, index) => {
      step.addEventListener('click', () => activate(index));
      step.addEventListener('pointerenter', () => warmPanelMedia(panels[index], true));
      step.addEventListener('focus', () => warmPanelMedia(panels[index], true));
      step.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % steps.length;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + steps.length) % steps.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = steps.length - 1;
        else return;
        event.preventDefault();
        activate(next, true);
      });
    });

    activate(0);
    const setStoryVisibility = (visible) => {
      section.dataset.calStoryVisible = String(visible);
      if (visible) {
        section.dataset.calStoryWarm = 'true';
        warmNearbyPanels();
        startPanelMedia(panels[activeIndex]);
      } else {
        panels.forEach(pausePanelMedia);
      }
    };
    const warmStoryMedia = () => {
      section.dataset.calStoryWarm = 'true';
      panels.forEach((panel, index) => warmPanelMedia(panel, index === activeIndex || index === activeIndex + 1));
    };
    if ('IntersectionObserver' in window) {
      const warmupObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        warmStoryMedia();
        warmupObserver.disconnect();
      }, { rootMargin: '1100px 0px' });
      const visibilityObserver = new IntersectionObserver((entries) => {
        setStoryVisibility(entries.some((entry) => entry.isIntersecting));
      }, { rootMargin: '260px 0px', threshold: .01 });
      warmupObserver.observe(section);
      visibilityObserver.observe(section);
    } else {
      setStoryVisibility(true);
      warmStoryMedia();
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') startPanelMedia(panels[activeIndex]);
      else panels.forEach(pausePanelMedia);
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  };

  const initDetails = (section) => {
    if (section.dataset.calDetailsReady === 'true') return;
    const tabs = [...section.querySelectorAll('[data-cal-details-tab]')];
    const panels = [...section.querySelectorAll('[data-cal-details-panel]')];
    const viewer = section.querySelector('[data-cal-details-viewer]');
    const product = section.querySelector('[data-cal-details-product]');
    const featureVideo = section.querySelector('[data-cal-details-video]');
    const replayButton = section.querySelector('[data-cal-details-replay]');
    const colors = section.querySelector('[data-cal-details-colors]');
    const colorButtons = [...section.querySelectorAll('button[data-cal-details-color]')];
    const label = section.querySelector('.cal-details__view-label[data-cal-details-label]');
    const mobilePrev = section.querySelector('[data-cal-details-mobile-prev]');
    const mobileNext = section.querySelector('[data-cal-details-mobile-next]');
    const mobileInfo = section.querySelector('[data-cal-details-mobile-info]');
    const mobileLabel = section.querySelector('[data-cal-details-mobile-label]');
    const mobileCount = section.querySelector('[data-cal-details-mobile-count]');
    const mobileSummary = section.querySelector('[data-cal-details-mobile-summary]');
    const mobileSummaryContent = section.querySelector('[data-cal-details-mobile-summary-content]');
    if (!tabs.length || !viewer) return;
    section.dataset.calDetailsReady = 'true';
    let activeIndex = 0;
    const preloadedColorScenes = new Set();
    const preloadedVideoSources = new Map();
    let videoPreloadBucket;
    const mobileVideoQuery = matchMedia('(max-width: 820px)');

    const setReplayButton = (visible) => {
      if (!replayButton) return;
      replayButton.classList.toggle('is-visible', visible);
      replayButton.setAttribute('aria-hidden', String(!visible));
      replayButton.tabIndex = visible ? 0 : -1;
    };

    const prepareFeatureVideo = () => {
      if (!featureVideo) return;
      featureVideo.autoplay = true;
      featureVideo.muted = true;
      featureVideo.defaultMuted = true;
      featureVideo.playsInline = true;
      featureVideo.preload = 'auto';
      featureVideo.setAttribute('autoplay', '');
      featureVideo.setAttribute('muted', '');
      featureVideo.setAttribute('playsinline', '');
      featureVideo.setAttribute('webkit-playsinline', '');
      featureVideo.setAttribute('preload', 'auto');
      featureVideo.loop = false;
      featureVideo.removeAttribute('loop');
    };

    const getResponsiveVideoData = (tab) => {
      const desktopSource = tab?.dataset.calDetailsSource || '';
      const mobileSource = tab?.dataset.calDetailsMobileSource || '';
      const desktopPoster = tab?.dataset.calDetailsPoster || '';
      const mobilePoster = tab?.dataset.calDetailsMobilePoster || '';
      const useMobile = mobileVideoQuery.matches;
      return {
        source: useMobile && mobileSource ? mobileSource : desktopSource,
        poster: useMobile && mobilePoster ? mobilePoster : desktopPoster,
        desktopSource,
        mobileSource,
        desktopPoster,
        mobilePoster
      };
    };

    const isDetailsVisible = () => {
      const rect = viewer.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < viewportHeight + 220 && rect.bottom > -220;
    };

    const warmPoster = (poster) => {
      if (!poster) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = poster;
    };

    const preloadVideoSource = (source, poster = '', eager = false) => {
      if (!source) return;
      warmPoster(poster);
      if (!videoPreloadBucket) {
        videoPreloadBucket = document.createElement('div');
        videoPreloadBucket.className = 'cal-details__video-preload';
        videoPreloadBucket.setAttribute('aria-hidden', 'true');
        videoPreloadBucket.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
        section.appendChild(videoPreloadBucket);
      }

      const cached = preloadedVideoSources.get(source);
      if (cached) {
        if (eager && cached.preload !== 'auto') {
          cached.preload = 'auto';
          cached.setAttribute('preload', 'auto');
          cached.load();
        }
        return;
      }

      const video = document.createElement('video');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = eager ? 'auto' : 'metadata';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', video.preload);
      if (poster) video.poster = poster;
      video.src = source;
      preloadedVideoSources.set(source, video);
      videoPreloadBucket.appendChild(video);
      video.load();
    };

    const warmDetailVideoTab = (index, eager = false) => {
      const tab = tabs[index];
      if (!tab || (tab.dataset.calDetailsKind || '') !== 'video') return false;
      const media = getResponsiveVideoData(tab);
      if (!media.source && !media.desktopSource && !media.mobileSource) return false;
      preloadVideoSource(media.source, media.poster, eager);
      // Do not download both desktop and mobile cuts of every detail film.
      // The active breakpoint is the only one a visitor can see; a later
      // resize naturally requests the alternate source when that tab opens.
      return true;
    };

    const warmUpcomingVideoTab = (eager = false) => {
      const searchOrder = [];
      for (let index = activeIndex; index < tabs.length; index += 1) searchOrder.push(index);
      for (let index = activeIndex - 1; index >= 0; index -= 1) searchOrder.push(index);
      const nextVideoIndex = searchOrder.find((index) => (tabs[index]?.dataset.calDetailsKind || '') === 'video');
      if (nextVideoIndex !== undefined) warmDetailVideoTab(nextVideoIndex, eager);
    };

    const preloadDetailVideos = (eager = false) => {
      let eagerUsed = false;
      tabs.forEach((tab, index) => {
        if ((tab.dataset.calDetailsKind || '') !== 'video') return;
        const activeOrNext = index === activeIndex || index === activeIndex + 1;
        warmDetailVideoTab(index, eager && activeOrNext && !eagerUsed);
        if (eager && activeOrNext) eagerUsed = true;
      });
    };

    const warmNearbyDetailVideos = (eager = false) => {
      warmDetailVideoTab(activeIndex, eager);
      warmDetailVideoTab(activeIndex + 1, eager);
      warmDetailVideoTab(activeIndex - 1, false);
      warmUpcomingVideoTab(eager);
    };

    const freezeFeatureVideo = () => {
      if (!featureVideo) return;
      const source = featureVideo.getAttribute('src') || '';
      if (!source) return;
      featureVideo.pause();
      setReplayButton(true);
    };

    const stopFeatureVideo = (releaseSource = false) => {
      if (!featureVideo) return;
      featureVideo.dataset.calDetailsPlayToken = '';
      pauseVideo(featureVideo);
      setReplayButton(!releaseSource && featureVideo.ended && viewer.classList.contains('is-video'));
      if (!releaseSource) return;
      featureVideo.removeAttribute('src');
      featureVideo.removeAttribute('poster');
      try { featureVideo.load(); } catch {}
    };

    const playFeatureVideo = (restart = true) => {
      if (!featureVideo || !featureVideo.getAttribute('src')) return;
      if (!isDetailsVisible() || document.visibilityState === 'hidden') {
        pauseVideo(featureVideo);
        return;
      }
      prepareFeatureVideo();
      const playToken = `${Date.now()}-${Math.random()}`;
      featureVideo.dataset.calDetailsPlayToken = playToken;
      const isCurrentPlay = () => featureVideo.dataset.calDetailsPlayToken === playToken;

      const tryPlay = (reset = false) => {
        if (!isCurrentPlay() || !featureVideo.getAttribute('src')) return;
        if (reset && featureVideo.readyState >= HTMLMediaElement.HAVE_METADATA) {
          try {
            featureVideo.currentTime = 0;
          } catch {
            // Some browsers reject seeking before enough media data is ready.
          }
        }
        setReplayButton(false);
        const playRequest = featureVideo.play();
        if (!playRequest || typeof playRequest.catch !== 'function') return;
        playRequest.catch(() => {
          if (!isCurrentPlay()) return;
          const retry = () => {
            if (!isCurrentPlay()) return;
            featureVideo.play().catch(() => {
              if (isCurrentPlay()) setReplayButton(true);
            });
          };
          featureVideo.addEventListener('loadeddata', retry, { once: true });
          featureVideo.addEventListener('canplay', retry, { once: true });
        });
      };

      if (restart && featureVideo.readyState < HTMLMediaElement.HAVE_METADATA) {
        featureVideo.addEventListener('loadedmetadata', () => tryPlay(true), { once: true });
      }
      requestAnimationFrame(() => tryPlay(restart));
    };

    const syncMobileDetails = (index) => {
      const tab = tabs[index];
      const panel = panels[index];
      if (!tab) return;
      if (mobileLabel) mobileLabel.textContent = tab.textContent.trim();
      if (mobileCount) mobileCount.textContent = `${String(index + 1).padStart(2, '0')} / ${String(tabs.length).padStart(2, '0')}`;
      if (mobilePrev) mobilePrev.disabled = index === 0;
      if (mobileNext) mobileNext.disabled = index === tabs.length - 1;
      if (mobileSummaryContent) {
        const source = panel?.querySelector('.cal-details__copy');
        mobileSummaryContent.innerHTML = source?.innerHTML || '';
      }
    };

    const setVideoSource = (source, poster = '') => {
      if (!featureVideo) return;
      if (!source) {
        stopFeatureVideo(true);
        return;
      }
      prepareFeatureVideo();
      if (poster) featureVideo.poster = poster;
      else featureVideo.removeAttribute('poster');
      const currentSource = featureVideo.getAttribute('src') || '';
      if (currentSource !== source) {
        featureVideo.pause();
        featureVideo.setAttribute('src', source || '');
        featureVideo.load();
      }
      preloadVideoSource(source, poster, true);
      setReplayButton(false);
      playFeatureVideo(true);
    };

    const setProduct = (source, alt) => {
      if (!product || !source) return;
      product.classList.remove('is-changing');
      void product.offsetWidth;
      product.classList.add('is-changing');
      product.src = source;
      if (alt) product.alt = alt;
    };

    const preloadColorScenes = () => {
      colorButtons.forEach((button) => {
        const source = button.dataset.calDetailsColorScene;
        if (!source || preloadedColorScenes.has(source)) return;
        preloadedColorScenes.add(source);
        const image = new Image();
        image.src = source;
      });
    };

    const setColorScene = (button, animate = false) => {
      if (!button) return;
      const source = button.dataset.calDetailsColorScene || button.dataset.calDetailsSource || '';
      if (!source) return;
      colorButtons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      viewer.dataset.calDetailsColor = button.dataset.calDetailsColorKey || '';
      if (animate) {
        viewer.classList.remove('is-color-changing');
        void viewer.offsetWidth;
        viewer.classList.add('is-color-changing');
        window.setTimeout(() => viewer.classList.remove('is-color-changing'), 390);
      }
      setProduct(source, button.dataset.calDetailsAlt || '');
      if (label) label.textContent = colors?.dataset.calDetailsColorsViewLabel || button.dataset.calDetailsLabel || '';
    };

    const activate = (index, focus = false) => {
      activeIndex = Math.max(0, Math.min(index, tabs.length - 1));
      const tab = tabs[activeIndex];
      const kind = tab.dataset.calDetailsKind || 'image';
      const media = getResponsiveVideoData(tab);
      const source = media.source;
      const poster = media.poster;
      const alt = tab.dataset.calDetailsAlt || '';
      tabs.forEach((item, current) => {
        const active = current === activeIndex;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
        item.closest('.cal-details__item')?.classList.toggle('is-open', active);
      });
      panels.forEach((panel, current) => {
        const active = current === activeIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      viewer.classList.toggle('is-video', kind === 'video');
      viewer.classList.toggle('is-colors', kind === 'colors');
      viewer.classList.toggle('is-image', kind === 'image');
      if (label) label.textContent = tab.dataset.calDetailsLabel || tab.textContent.trim();
      if (colors) colors.hidden = kind !== 'colors';
      if (kind === 'video') {
        setVideoSource(source, poster);
      } else {
        stopFeatureVideo(true);
        if (kind === 'colors') {
          const selectedColor = colorButtons.find((button) => button.getAttribute('aria-pressed') === 'true') || colorButtons[0];
          preloadColorScenes();
          setColorScene(selectedColor);
        } else {
          setProduct(source, alt);
        }
      }
      if (section.dataset.calDetailsWarm === 'true' || isDetailsVisible()) warmNearbyDetailVideos(true);
      syncMobileDetails(activeIndex);
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      const warmTabVideo = () => {
        if ((tab.dataset.calDetailsKind || '') !== 'video') return;
        const media = getResponsiveVideoData(tab);
        preloadVideoSource(media.source, media.poster, true);
      };
      tab.addEventListener('pointerenter', warmTabVideo, { passive: true });
      tab.addEventListener('focus', warmTabVideo);
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(next, true);
      });
    });
    colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if ((tabs[activeIndex].dataset.calDetailsKind || '') === 'colors') {
          setColorScene(button, true);
        }
      });
    });
    if (featureVideo) {
      prepareFeatureVideo();
      featureVideo.addEventListener('play', () => setReplayButton(false));
      featureVideo.addEventListener('ended', freezeFeatureVideo);
      featureVideo.addEventListener('canplay', () => {
        if (viewer.classList.contains('is-video') && !featureVideo.ended) playFeatureVideo(false);
      });
    }
    replayButton?.addEventListener('click', () => {
      if (!featureVideo || !featureVideo.getAttribute('src')) return;
      setReplayButton(false);
      playFeatureVideo(true);
    });
    mobilePrev?.addEventListener('click', () => activate(Math.max(0, activeIndex - 1)));
    mobileNext?.addEventListener('click', () => activate(Math.min(tabs.length - 1, activeIndex + 1)));
    mobileInfo?.addEventListener('click', () => {
      const open = mobileInfo.getAttribute('aria-expanded') !== 'true';
      mobileInfo.setAttribute('aria-expanded', String(open));
      mobileSummary?.classList.toggle('is-open', open);
    });

    let touchX = 0;
    let touchY = 0;
    let touchEnabled = false;
    viewer.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchEnabled = !event.target.closest('button');
      touchX = touch.clientX;
      touchY = touch.clientY;
    }, { passive: true });
    viewer.addEventListener('touchend', (event) => {
      if (!mobileVideoQuery.matches || !touchEnabled) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchX;
      const deltaY = touch.clientY - touchY;
      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      activate(deltaX < 0 ? Math.min(tabs.length - 1, activeIndex + 1) : Math.max(0, activeIndex - 1));
    }, { passive: true });

    const setDetailsVisibility = (visible) => {
      section.dataset.calDetailsVisible = String(visible);
      if (visible) {
        section.dataset.calDetailsWarm = 'true';
        warmNearbyDetailVideos(true);
        if (viewer.classList.contains('is-video') && featureVideo && !featureVideo.ended) playFeatureVideo(false);
        else if (viewer.classList.contains('is-video') && featureVideo?.ended) setReplayButton(true);
      } else {
        stopFeatureVideo(false);
      }
    };

    if ('IntersectionObserver' in window) {
      const videoWarmupObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        section.dataset.calDetailsWarm = 'true';
        preloadDetailVideos(false);
        warmNearbyDetailVideos(true);
        videoWarmupObserver.disconnect();
      }, { rootMargin: '1100px 0px' });
      const videoVisibilityObserver = new IntersectionObserver((entries) => {
        setDetailsVisibility(entries.some((entry) => entry.isIntersecting));
      }, { rootMargin: '220px 0px', threshold: .01 });
      videoWarmupObserver.observe(section);
      videoVisibilityObserver.observe(section);
    } else {
      setDetailsVisibility(true);
      preloadDetailVideos(false);
      warmNearbyDetailVideos(true);
    }

    let resizeFrame;
    window.addEventListener('resize', () => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = undefined;
        if ((tabs[activeIndex]?.dataset.calDetailsKind || '') === 'video') activate(activeIndex);
      });
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && viewer.classList.contains('is-video') && featureVideo && !featureVideo.ended) {
        playFeatureVideo(false);
      } else if (document.visibilityState === 'hidden') {
        stopFeatureVideo(false);
      }
    });
    activate(0);
  };

  const initMedia13 = (section) => {
    if (!section || section.getAttribute('data-cal-media-13-ready') === 'true') return;
    const track = section.querySelector('[data-cal-media-13-track]');
    const viewport = section.querySelector('[data-cal-media-13-viewport]');
    const slides = [...section.querySelectorAll('[data-cal-media-13-slide]')];
    const dots = [...section.querySelectorAll('[data-cal-media-13-dot]')];
    const previous = section.querySelector('[data-cal-media-13-prev]');
    const next = section.querySelector('[data-cal-media-13-next]');
    if (!track || !viewport || !slides.length) return;

    section.setAttribute('data-cal-media-13-ready', 'true');
    let index = 0;
    let timer = null;
    let startX = 0;
    let deltaX = 0;
    let dragging = false;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    const select = (nextIndex, animate = true) => {
      index = (nextIndex + slides.length) % slides.length;
      track.style.transition = animate && !reducedMotion.matches ? '' : 'none';
      track.style.transform = `translate3d(-${index * (100 / slides.length)}%, 0, 0)`;
      slides.forEach((slide, itemIndex) => {
        const active = itemIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((control) => {
          control.tabIndex = active ? 0 : -1;
        });
      });
      dots.forEach((dot, itemIndex) => {
        const active = itemIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    };

    const getAutoplaySpeed = () => {
      const speedAttr = section.getAttribute('data-cal-media-13-speed');
      const speed = parseFloat(speedAttr);
      return (Number.isFinite(speed) && speed > 0 ? speed : 3) * 1000;
    };

    const isAutoplayEnabled = () => {
      const val = section.getAttribute('data-cal-media-13-autoplay');
      return val === 'true' || val === true || val === '';
    };

    const stopAutoplay = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const restartAutoplay = () => {
      stopAutoplay();
      if (!isAutoplayEnabled() || slides.length < 2) return;
      timer = setInterval(() => select(index + 1), getAutoplaySpeed());
    };

    previous?.addEventListener('click', () => { select(index - 1); restartAutoplay(); });
    next?.addEventListener('click', () => { select(index + 1); restartAutoplay(); });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      const dotIndex = Number(dot.getAttribute('data-cal-media-13-index') ?? dot.dataset['calMedia-13Index']) || 0;
      select(dotIndex);
      restartAutoplay();
    }));

    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', restartAutoplay);

    track.addEventListener('pointerdown', (event) => {
      if (event.target.closest('a, button')) return;
      stopAutoplay();
      dragging = true;
      startX = event.clientX;
      deltaX = 0;
      track.setPointerCapture?.(event.pointerId);
      track.style.transition = 'none';
    });
    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      deltaX = event.clientX - startX;
      const percent = (deltaX / viewport.clientWidth) * (100 / slides.length);
      track.style.transform = `translate3d(calc(-${index * (100 / slides.length)}% + ${percent}%), 0, 0)`;
    });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      select(Math.abs(deltaX) > 45 ? index + (deltaX < 0 ? 1 : -1) : index);
      restartAutoplay();
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);
    reducedMotion.addEventListener?.('change', restartAutoplay);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else restartAutoplay();
    });

    section.addEventListener('shopify:block:select', (event) => {
      const blockSlide = event.target.closest?.('[data-cal-media-13-slide]') || (event.target.matches?.('[data-cal-media-13-slide]') ? event.target : null);
      if (blockSlide) {
        const blockIndex = slides.indexOf(blockSlide);
        if (blockIndex >= 0) select(blockIndex);
      }
      stopAutoplay();
    });
    section.addEventListener('shopify:block:deselect', restartAutoplay);

    select(0, false);
    restartAutoplay();
  };

  const initNavigation = () => {
    const nav = document.querySelector('[data-cal-lab-nav]');
    if (!nav || nav.dataset.calNavigationReady === 'true') return;
    const links = [...nav.querySelectorAll('[data-cal-nav-link]')];
    const getAnchorTarget = (link) => {
      const href = link.getAttribute('href');
      if (!href) return null;
      let destination;
      try {
        destination = new URL(href, window.location.href);
      } catch {
        return null;
      }
      if (!destination.hash || destination.origin !== window.location.origin || destination.pathname !== window.location.pathname) return null;
      return document.getElementById(decodeURIComponent(destination.hash.slice(1)));
    };
    const targets = links.map(getAnchorTarget).filter(Boolean);
    if (!links.length || !targets.length) return;
    nav.dataset.calNavigationReady = 'true';
    const navSection = nav.closest('.shopify-section');
    const header = document.querySelector('sticky-header, .header-wrapper, header');
    const anchor = document.createElement('span');
    anchor.className = 'cal-lab-nav__anchor';
    anchor.setAttribute('aria-hidden', 'true');
    nav.before(anchor);

    let stickyFrame;
    let lastHeaderOffset = -1;
    let lastNavHeight = -1;
    const updateStickyNav = () => {
      const headerRect = header?.getBoundingClientRect();
      const headerOffset = headerRect && headerRect.bottom > 0 ? Math.round(headerRect.bottom) : 0;
      const navHeight = Math.round(nav.getBoundingClientRect().height || 58);
      const shouldFix = anchor.getBoundingClientRect().top <= headerOffset;
      if (nav.classList.contains('is-fixed') !== shouldFix) nav.classList.toggle('is-fixed', shouldFix);
      if (navSection && navHeight !== lastNavHeight) navSection.style.minHeight = `${navHeight}px`;
      if (headerOffset !== lastHeaderOffset) document.documentElement.style.setProperty('--cal-header-offset', `${headerOffset}px`);
      if (navHeight !== lastNavHeight) document.documentElement.style.setProperty('--cal-nav-height', `${navHeight}px`);
      lastHeaderOffset = headerOffset;
      lastNavHeight = navHeight;
    };
    const requestStickyNavUpdate = () => {
      if (stickyFrame) return;
      stickyFrame = requestAnimationFrame(() => {
        stickyFrame = undefined;
        updateStickyNav();
      });
    };
    const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const activateLink = (id) => {
      links.forEach((link) => {
        const active = getAnchorTarget(link)?.id === id;
        link.classList.toggle('is-active', active);
        link.toggleAttribute('aria-current', active);
        if (active && window.innerWidth <= 820 && typeof link.scrollIntoView === 'function') {
          link.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    };
    const scrollToAnchor = (target) => {
      updateStickyNav();
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      const headerOffset = Math.max(0, Math.round(headerBottom));
      const navHeight = Math.round(nav.getBoundingClientRect().height || 0);
      const breathingRoom = window.innerWidth <= 820 ? 0 : 18;
      const top = window.scrollY + target.getBoundingClientRect().top - headerOffset - navHeight - breathingRoom;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    // The reference page uses a smooth hash transition. Handle both the lab
    // navigation and hero CTAs so it remains smooth when a Shopify URL setting
    // outputs the current page URL plus a hash instead of only "#section".
    const anchorLinks = [...new Set([...links, ...document.querySelectorAll('.cal-hero__actions a[href*="#"]')])];
    anchorLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = getAnchorTarget(link);
        if (!target) return;
        event.preventDefault();
        window.history.pushState(null, '', `#${target.id}`);
        scrollToAnchor(target);
      });
    });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const active = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (active?.target?.id) activateLink(active.target.id);
      }, { rootMargin: '-35% 0px -55% 0px', threshold: 0 });
      targets.forEach((target) => observer.observe(target));
    }
    updateStickyNav();
    window.addEventListener('scroll', requestStickyNavUpdate, { passive: true });
    window.addEventListener('resize', requestStickyNavUpdate, { passive: true });
  };

  const initCompareSpecToggles = () => {
    document.querySelectorAll('[data-cal-compare-spec-toggle]').forEach((button) => {
      if (button.dataset.calCompareToggleReady === 'true') return;
      button.dataset.calCompareToggleReady = 'true';
      button.addEventListener('click', () => {
        const card = button.closest('.cal-compare__card');
        if (!card) return;
        const expanded = !card.classList.contains('specs-expanded');
        card.classList.toggle('specs-expanded', expanded);
        button.setAttribute('aria-expanded', String(expanded));
      });
    });
  };

  const initCompareVariants = () => {
    document.querySelectorAll('[data-cal-compare-product-card]').forEach((card) => {
      if (card.dataset.calCompareVariantsReady === 'true') return;
      const swatches = [...card.querySelectorAll('[data-cal-compare-swatch]')];
      if (!swatches.length) return;
      card.dataset.calCompareVariantsReady = 'true';

      const form = card.querySelector('.cal-compare__add-form');
      const variantInput = form?.querySelector('[name="id"]');
      const submitButton = form?.querySelector('[type="submit"]');
      const colorLabel = card.querySelector('[data-cal-compare-color-label]');
      const visual = card.querySelector('.cal-compare__visual');
      const image = visual?.querySelector(':scope > img:not([data-cal-compare-variant-image])');
      const variantImage = visual?.querySelector('[data-cal-compare-variant-image]');
      const lineup = visual?.querySelector('.cal-compare__lineup');

      const selectVariant = (swatch) => {
        const variantId = swatch.dataset.variantId;
        if (!variantId) return;

        swatches.forEach((item) => {
          const selected = item === swatch;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        if (variantInput && variantId && /^\d+$/.test(String(variantId).trim())) {
          variantInput.value = variantId;
        }
        if (submitButton) {
          const available = swatch.dataset.available !== 'false';
          submitButton.toggleAttribute('disabled', !available);
          submitButton.toggleAttribute('aria-disabled', !available);
          if (variantId && /^\d+$/.test(String(variantId).trim())) {
            submitButton.dataset.variantId = variantId;
          }
        }
        if (colorLabel) colorLabel.textContent = `Color: ${swatch.dataset.colorLabel}`;

        const imageUrl = swatch.dataset.imageUrl;
        if (variantImage && imageUrl) {
          lineup?.setAttribute('hidden', '');
          image?.setAttribute('hidden', '');
          variantImage.src = imageUrl;
          variantImage.removeAttribute('hidden');
        } else if (image && imageUrl) {
          image.src = imageUrl;
          image.removeAttribute('srcset');
        }
      };

      swatches.forEach((swatch) => swatch.addEventListener('click', () => selectVariant(swatch)));
    });
  };

  // Compare cards use one add request and reuse the returned cart-drawer
  // section. The global ProductForm makes a second /cart.js request, which
  // is unnecessary here because the add response already contains sections.
  const initCompareFastAdd = () => {
    document.querySelectorAll('.cal-compare__add-form--fast').forEach((form) => {
      if (form.dataset.calCompareFastAddReady === 'true') return;
      form.dataset.calCompareFastAddReady = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (form.dataset.calCompareFastAddPending === 'true') return;

        const button = form.querySelector('[type="submit"]');
        const errorMessage = form.querySelector('.product-form__error-message');
        const variantInput = form.querySelector('[name="id"]');
        if (!button || !variantInput?.value) return;

        form.dataset.calCompareFastAddPending = 'true';
        errorMessage?.toggleAttribute('hidden', true);
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');

        try {
          const sectionsToBundle = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));

          const config = theme.utils.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(form);
          formData.append('sections', sectionsToBundle.join(','));
          formData.append('sections_url', window.location.pathname);
          config.body = formData;

          const response = await fetch(theme.routes.cart_add_url, config);
          const parsedState = await response.json();
          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || parsedState.message || 'Unable to add this product to cart.');
          }

          const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
          const currentCount = [...document.querySelectorAll('cart-count')]
            .map((counter) => Number.parseInt(counter.textContent, 10))
            .find(Number.isFinite) || 0;
          const cart = {
            ...parsedState,
            item_count: parsedState.item_count ?? currentCount + quantity,
            sections: parsedState.sections || {}
          };

          // CartItems handles this when cart.js is already loaded. The same
          // event also keeps cart-count elements and other theme listeners in
          // sync without dispatching ajaxProduct:added (which forces a second
          // cart refresh in the lazy drawer loader).
          if (theme.pubsub?.publish) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'ca-compare-product-form',
              productVariantId: variantInput.value,
              cart
            });
          }

          document.querySelectorAll('cart-count').forEach((counter) => {
            counter.textContent = String(cart.item_count);
            counter.toggleAttribute('hidden', cart.item_count === 0);
          });

          const drawer = document.querySelector('cart-drawer');
          const drawerSectionId = drawer?.getAttribute('data-section-id');
          const drawerMarkup = drawerSectionId && cart.sections[drawerSectionId];
          if (drawerMarkup) {
            const parsedDrawer = new DOMParser().parseFromString(drawerMarkup, 'text/html');
            const updatedMiniCart = parsedDrawer.querySelector(`#MiniCart-${drawerSectionId}`);
            const currentMiniCart = document.querySelector(`#MiniCart-${drawerSectionId}`);
            if (updatedMiniCart && currentMiniCart) currentMiniCart.innerHTML = updatedMiniCart.innerHTML;
          }

          if (drawer && typeof drawer.show === 'function') {
            drawer.show(button);
          } else {
            document.querySelector('[aria-controls="CartDrawer"]')?.click();
          }
        } catch (error) {
          if (errorMessage) {
            errorMessage.textContent = error.message;
            errorMessage.removeAttribute('hidden');
          }
        } finally {
          form.dataset.calCompareFastAddPending = 'false';
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
        }
      });
    });
  };

  const initSegmentedMedia = () => {
    document.querySelectorAll('[data-cal-media-start]').forEach((host) => {
      if (host.dataset.calSegmentReady === 'true') return;
      const video = host.querySelector('video');
      const start = Number(host.dataset.calMediaStart);
      const end = Number(host.dataset.calMediaEnd);
      if (!Number.isFinite(start)) return;
      if (!video) {
        const deferredMedia = host.querySelector('video-media');
        if (deferredMedia && host.dataset.calSegmentWaiting !== 'true') {
          host.dataset.calSegmentWaiting = 'true';
          deferredMedia.addEventListener('video:play', () => {
            delete host.dataset.calSegmentWaiting;
            initSegmentedMedia();
          }, { once: true });
        }
        return;
      }
      host.dataset.calSegmentReady = 'true';
      const reset = () => {
        if (Number.isFinite(end) && video.currentTime >= end) video.currentTime = start;
      };
      const setStart = () => {
        if (video.currentTime < start || (Number.isFinite(end) && video.currentTime >= end)) video.currentTime = start;
        if (!host.hasAttribute('data-cal-gallery-media') || host.dataset.calGalleryVisible === 'true') playVideo(video);
      };
      video.addEventListener('loadedmetadata', setStart, { once: true });
      video.addEventListener('timeupdate', reset);
      if (video.readyState >= 1) setStart();
    });
  };

  const initDeferredMedia = () => {
    document.querySelectorAll('video-media.cal-media').forEach((media) => {
      if (media.dataset.calPosterReady === 'true') return;
      media.dataset.calPosterReady = 'true';

      const bindVideo = () => {
        const video = media.querySelector('video');
        if (!video || video.dataset.calPosterListener === 'true') return false;
        video.dataset.calPosterListener = 'true';
        const reveal = () => media.setAttribute('data-cal-video-ready', 'true');
        if (media.hasAttribute('data-cal-hero-media')) {
          video.addEventListener('playing', reveal, { once: true });
        } else {
          video.addEventListener('loadeddata', reveal, { once: true });
          video.addEventListener('canplay', reveal, { once: true });
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) reveal();
        }
        return true;
      };

      if (bindVideo()) return;
      const videoObserver = new MutationObserver(() => {
        if (bindVideo()) videoObserver.disconnect();
      });
      videoObserver.observe(media, { childList: true, subtree: true });

      // The Hero is the only film allowed to join the initial loading path.
      // Its high-priority poster paints immediately; creating the player here
      // starts video buffering before the visitor can scroll.
      if (media.hasAttribute('data-cal-media-priority') && document.visibilityState !== 'hidden') {
        playMedia(media);
      }
    });
  };

  const initMobileMediaWarmup = () => {
    if (!matchMedia('(max-width: 820px)').matches) return;
    document.querySelectorAll('video-media.cal-media').forEach((media) => {
      if (media.dataset.calMobileWarmupReady === 'true') return;
      media.dataset.calMobileWarmupReady = 'true';

      const warm = () => {
        if (media.dataset.calMobileMediaWarmed === 'true') return;
        media.dataset.calMobileMediaWarmed = 'true';
        warmPosterImages(media);
        createDeferredMediaPlayer(media);
        const video = media.querySelector('video');
        if (video && (!video.preload || video.preload === 'none')) {
          video.preload = 'metadata';
          video.setAttribute('preload', 'metadata');
          try { video.load(); } catch {}
        }
      };

      // The hero remains the one exception: it receives an immediate poster
      // and player so the first screen never waits on an observer. Every
      // lower video stays lazy until it is within one screen of the viewport.
      if (media.hasAttribute('data-cal-media-priority')) {
        warmPosterImages(media, true);
        createDeferredMediaPlayer(media);
      }
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          warm();
          observer.disconnect();
        }, { rootMargin: '700px 0px', threshold: 0 });
        observer.observe(media);
      } else if (!media.hasAttribute('data-cal-media-priority')) {
        warm();
      }
    });
  };

  const initGalleryMedia = () => {
    document.querySelectorAll('.cal-grid--gallery').forEach((section) => {
      if (section.dataset.calGallerySectionReady === 'true') return;
      const hosts = [...section.querySelectorAll('[data-cal-gallery-media]')];
      if (!hosts.length) return;
      section.dataset.calGallerySectionReady = 'true';

      const controllers = hosts.map((host) => {
        const media = host.querySelector('video-media') || host.querySelector('video');
        if (!media) return null;
        host.dataset.calGalleryReady = 'true';

        const nativeVideo = () => media.tagName === 'VIDEO' ? media : media.querySelector('video');
        const createDeferredVideo = () => {
          if (media.tagName !== 'VIDEO-MEDIA' || nativeVideo()) return;
          const create = () => {
            if (!media.isConnected || nativeVideo()) return;
            if (typeof media.playerTarget === 'function') void media.player;
          };
          if (customElements.get('video-media')) create();
          else customElements.whenDefined('video-media').then(create);
        };
        const prepareVideo = () => {
          const video = nativeVideo();
          if (!video) return false;
          if (video.dataset.calGalleryVideoReady === 'true') return true;
          video.dataset.calGalleryVideoReady = 'true';
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.loop = true;
          video.preload = 'metadata';
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('loop', '');
          video.setAttribute('preload', 'metadata');
          video.addEventListener('playing', () => media.closest('video-media')?.setAttribute('data-cal-video-ready', 'true'));
          return true;
        };
        const bindWhenReady = () => {
          if (prepareVideo()) {
            initSegmentedMedia();
            return;
          }
          if (media.tagName !== 'VIDEO-MEDIA' || host.dataset.calGalleryBinding === 'true') return;
          host.dataset.calGalleryBinding = 'true';
          const observer = new MutationObserver(() => {
            if (!prepareVideo()) return;
            delete host.dataset.calGalleryBinding;
            observer.disconnect();
            initSegmentedMedia();
          });
          observer.observe(media, { childList: true, subtree: true });
        };
        const warm = () => {
          createDeferredVideo();
          bindWhenReady();
        };
        const start = () => {
          if (document.visibilityState === 'hidden') return;
          host.dataset.calGalleryVisible = 'true';
          if (media.tagName === 'VIDEO-MEDIA') media.paused = false;
          warm();
          playMedia(media);
          const video = nativeVideo();
          if (video) {
            video.preload = 'auto';
            video.setAttribute('preload', 'auto');
            playVideo(video);
          }
        };
        const pause = () => {
          host.dataset.calGalleryVisible = 'false';
          if (media.tagName === 'VIDEO-MEDIA') media.paused = true;
          pauseMedia(media);
          pauseVideo(nativeVideo());
        };

        bindWhenReady();
        return { host, warm, start, pause };
      }).filter(Boolean);

      const warmAll = () => controllers.forEach((controller) => controller.warm());
      const pauseAll = () => controllers.forEach((controller) => controller.pause());
      const isHostForeground = (host) => {
        const rect = host.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        return visibleWidth * visibleHeight >= rect.width * rect.height * .5;
      };

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          warmAll();
          observer.disconnect();
        }, { rootMargin: '900px 0px' }).observe(section);
        controllers.forEach((controller) => {
          new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (entry?.isIntersecting && entry.intersectionRatio >= .5) controller.start();
            else controller.pause();
          }, { threshold: [.5] }).observe(controller.host);
        });
      } else {
        warmAll();
        controllers.forEach((controller) => controller.start());
      }

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          controllers.forEach((controller) => {
            if (isHostForeground(controller.host)) controller.start();
            else controller.pause();
          });
        } else {
          pauseAll();
        }
      });
    });
  };

  const initDesignReplay = () => {
    document.querySelectorAll('[data-cal-design-replay]').forEach((host) => {
      if (host.dataset.calDesignReplayReady === 'true') return;
      const replayButton = host.querySelector('[data-cal-design-replay-button]');
      if (!replayButton) return;
      host.dataset.calDesignReplayReady = 'true';
      host.dataset.calDesignPlayedOnce = 'false';
      host.dataset.calDesignInView = 'false';

      const deferredMedia = host.querySelector('video-media');
      const setReplayButton = (visible) => {
        replayButton.classList.toggle('is-visible', visible);
        replayButton.setAttribute('aria-hidden', String(!visible));
        replayButton.tabIndex = visible ? 0 : -1;
      };
      const getVideo = () => host.querySelector('video');
      const createDeferredPlayer = () => {
        if (!deferredMedia || getVideo()) return;
        const create = () => {
          if (!deferredMedia.isConnected || getVideo()) return;
          if (typeof deferredMedia.playerTarget === 'function') void deferredMedia.player;
        };
        if (customElements.get('video-media')) create();
        else customElements.whenDefined('video-media').then(create);
      };
      const whenVideoReady = (callback) => {
        const video = getVideo();
        if (video) {
          callback(video);
          return;
        }
        if (!deferredMedia) return;
        const videoObserver = new MutationObserver(() => {
          const createdVideo = getVideo();
          if (!createdVideo) return;
          videoObserver.disconnect();
          callback(createdVideo);
        });
        videoObserver.observe(host, { childList: true, subtree: true });
        createDeferredPlayer();
      };
      const pauseCurrentVideo = () => {
        if (deferredMedia) {
          deferredMedia.paused = true;
          pauseMedia(deferredMedia);
        }
        pauseVideo(getVideo());
      };
      const requestVideoPlay = (video) => {
        if (!video || typeof video.play !== 'function') return;
        video.play().catch(() => setReplayButton(true));
      };
      const playOnce = () => {
        if (host.dataset.calDesignInView !== 'true' || host.dataset.calDesignPlayedOnce === 'true') return;
        setReplayButton(false);
        if (deferredMedia) {
          deferredMedia.paused = false;
          playMedia(deferredMedia);
        }
        whenVideoReady((video) => {
          bindVideo();
          if (host.dataset.calDesignInView !== 'true' || host.dataset.calDesignPlayedOnce === 'true') return;
          requestVideoPlay(video);
        });
      };
      const replay = () => {
        host.dataset.calDesignPlayedOnce = 'false';
        setReplayButton(false);
        if (deferredMedia) {
          deferredMedia.paused = false;
          playMedia(deferredMedia);
        }
        whenVideoReady((video) => {
          bindVideo();
          video.currentTime = 0;
          requestVideoPlay(video);
        });
      };

      const bindVideo = () => {
        const video = getVideo();
        if (!video) return false;
        if (video.dataset.calDesignReplayVideoReady === 'true') return true;
        video.dataset.calDesignReplayVideoReady = 'true';
        video.loop = false;
        video.removeAttribute('loop');
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');

        video.addEventListener('play', () => setReplayButton(false));
        video.addEventListener('ended', () => {
          video.pause();
          video.dataset.calDesignPlayedOnce = 'true';
          host.dataset.calDesignPlayedOnce = 'true';
          if (deferredMedia) {
            deferredMedia.paused = true;
            deferredMedia.removeAttribute('playing');
          }
          setReplayButton(true);
        });
        if (video.ended || video.dataset.calDesignPlayedOnce === 'true' || host.dataset.calDesignPlayedOnce === 'true') setReplayButton(true);
        return true;
      };

      if (!bindVideo()) {
        const videoObserver = new MutationObserver(() => {
          if (bindVideo()) videoObserver.disconnect();
        });
        videoObserver.observe(host, { childList: true, subtree: true });
        deferredMedia?.addEventListener('video:play', bindVideo);
      }

      replayButton.addEventListener('click', replay);

      if ('IntersectionObserver' in window) {
        const warmupObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          createDeferredPlayer();
          warmupObserver.disconnect();
        }, { rootMargin: '900px 0px', threshold: 0 });
        const playObserver = new IntersectionObserver((entries) => {
          const visible = entries.some((entry) => entry.isIntersecting);
          host.dataset.calDesignInView = String(visible);
          if (visible) playOnce();
          else pauseCurrentVideo();
        }, { rootMargin: '120px 0px', threshold: .08 });
        warmupObserver.observe(host);
        playObserver.observe(host);
      } else {
        host.dataset.calDesignInView = 'true';
        createDeferredPlayer();
        playOnce();
      }

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') playOnce();
        else pauseCurrentVideo();
      });
      window.addEventListener('pageshow', playOnce);
    });
  };

  const initAutoplayMedia = () => {
    const prepareVideo = (video) => {
      if (!video || video.dataset.calAutoplayReady === 'true') return;
      video.dataset.calAutoplayReady = 'true';
      video.autoplay = true;
      video.setAttribute('autoplay', '');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      const start = () => {
        if (document.visibilityState === 'hidden') return;
        playVideo(video);
      };
      video.addEventListener('loadeddata', start);
      video.addEventListener('canplay', start);
      video.addEventListener('playing', () => { video.dataset.calAutoplayPlaying = 'true'; });
      video.addEventListener('pause', () => { if (!video.ended) video.dataset.calAutoplayPlaying = 'false'; });
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) start();

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          start();
        }, { rootMargin: '160px 0px' }).observe(video);
      }
      window.addEventListener('pageshow', start);
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') start(); });
    };

    document.querySelectorAll('.cal-design__visual--context video, .cal-design__video video').forEach((video) => {
      if (video.closest('[data-cal-design-replay]')) return;
      prepareVideo(video);
    });

    document.querySelectorAll('.cal-design__video video-media[autoplay]').forEach((media) => {
      if (media.dataset.calAutoplayMediaReady === 'true') return;
      media.dataset.calAutoplayMediaReady = 'true';

      const start = () => {
        if (document.visibilityState === 'hidden') return;
        media.paused = false;
        playMedia(media);
        prepareVideo(media.querySelector('video'));
      };

      const requestStart = () => requestAnimationFrame(start);
      media.addEventListener('video:play', requestStart);
      media.addEventListener('loadeddata', requestStart);
      media.addEventListener('canplay', requestStart);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          start();
        }, { rootMargin: '180px 0px' }).observe(media);
      } else {
        start();
      }

      customElements.whenDefined('video-media').then(() => {
        if (!media.isConnected) return;
        const rect = media.getBoundingClientRect();
        if (rect.top < window.innerHeight + 180 && rect.bottom > -180) start();
      });
      window.addEventListener('pageshow', start);
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') start(); });
    });
  };

  const initCompactFaq = () => {
    if (document.documentElement.dataset.calCompactFaqReady === 'true') return;
    document.documentElement.dataset.calCompactFaqReady = 'true';
    const compactPhoneQuery = matchMedia('(max-width: 390px)');
    const collapse = () => {
      if (!compactPhoneQuery.matches) return;
      document.querySelectorAll('#faq.cal-faq details[open]').forEach((item) => item.removeAttribute('open'));
    };
    collapse();
    if (compactPhoneQuery.addEventListener) compactPhoneQuery.addEventListener('change', collapse);
    else compactPhoneQuery.addListener(collapse);
  };

  const initCtaWordField = (section) => {
    if (section.dataset.calCtaReady === 'true') return;
    const field = section.querySelector('.cal-cta__word-field');
    const mainWords = [...section.querySelectorAll('.cal-cta__word-stream:not(.cal-cta__word-stream--secondary) span')].slice(0, 20);
    const secondaryWords = [...section.querySelectorAll('.cal-cta__word-stream--secondary span')].slice(0, 12);
    if (!field || !mainWords.length || !secondaryWords.length) return;
    section.dataset.calCtaReady = 'true';

    const points = [
      [-94, -170, -64, -8, .68], [-72, -115, -50, -6, .76], [-40, -58, -32, -4, .88],
      [-8, -8, -12, -1, 1], [20, 38, 12, 2, 1.08], [34, 68, 32, 4, 1.12],
      [30, 45, 50, 5, 1.06], [8, 2, 66, 3, .96], [-22, -52, 48, 0, .86],
      [-52, -96, 28, -3, .78], [-78, -138, 8, -6, .72], [-96, -178, -22, -9, .66]
    ];
    let visible = !('IntersectionObserver' in window);
    let width = 1;
    let height = 1;
    let spacing = 64;
    let loopHeight = 768;
    let mainWordWidths = [];
    let secondaryWordWidths = [];
    let animationFrame;
    let reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    const refreshMetrics = () => {
      width = Math.max(field.clientWidth, 1);
      height = Math.max(field.clientHeight, 1);
      const size = parseFloat(getComputedStyle(mainWords[0]).fontSize) || 72;
      const mobile = window.innerWidth <= 820;
      spacing = mobile ? Math.max(size * .72, (height + size) / 19) : size * .76;
      loopHeight = spacing * (mobile ? 20 : 12);
      mainWordWidths = mainWords.map((word) => word.offsetWidth);
      secondaryWordWidths = secondaryWords.map((word) => word.offsetWidth);
    };
    const wrap = (value, length) => ((value % length) + length) % length;
    const interpolate = (start, end, amount) => start + (end - start) * amount;
    const shapeAt = (y) => {
      const position = Math.max(0, Math.min(1, (y + spacing) / (height + spacing * 2))) * (points.length - 1);
      const index = Math.floor(position);
      const next = Math.min(index + 1, points.length - 1);
      const amount = position - index;
      return points[index].map((value, pointIndex) => interpolate(value, points[next][pointIndex], amount));
    };
    const place = (word, wordWidth, y, shape, center, mirrored) => {
      const [x, z, ry, rz, scale] = shape;
      const direction = mirrored ? -1 : 1;
      word.style.transform = `translate3d(${(center - wordWidth / 2).toFixed(2)}px, ${y.toFixed(2)}px, ${(z * .28).toFixed(2)}px) rotateY(${(ry * direction).toFixed(2)}deg) rotateZ(${(rz * direction).toFixed(2)}deg) scale(${scale.toFixed(3)})`;
    };
    const render = (time = 0) => {
      const mobile = window.innerWidth <= 820;
      const count = mobile ? 20 : 12;
      const progress = reducedMotion ? 0 : (time % (mobile ? 16000 : 10000)) / (mobile ? 16000 : 10000);
      const offset = progress * loopHeight;
      const horizontalScale = mobile ? .85 : Math.max(.52, Math.min(1, width / 1200));
      const anchor = width * (mobile ? .72 : .58);
      mainWords.forEach((word, index) => {
        if (index >= count) return;
        const y = wrap(index * spacing - offset, loopHeight) - spacing;
        const shape = shapeAt(y);
        place(word, mainWordWidths[index] || 0, y, shape, anchor + shape[0] * horizontalScale, false);
      });
      secondaryWords.forEach((word, index) => {
        const y = wrap(index * spacing + offset, loopHeight) - spacing;
        const shape = shapeAt(y);
        const wordWidth = secondaryWordWidths[index] || 0;
        const visibleWidth = wordWidth * shape[4] * Math.max(.22, Math.abs(Math.cos(shape[2] * Math.PI / 180)));
        place(word, wordWidth, y, shape, anchor + shape[0] * horizontalScale + visibleWidth + 6, true);
      });
    };

    refreshMetrics();
    const canAnimate = () => visible && !reducedMotion && document.visibilityState === 'visible';
    const stopAnimation = () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
    };
    const animate = (time) => {
      animationFrame = undefined;
      render(time);
      if (canAnimate()) animationFrame = requestAnimationFrame(animate);
    };
    const startAnimation = () => {
      if (!canAnimate() || animationFrame) return;
      animationFrame = requestAnimationFrame(animate);
    };
    const refreshAndRender = () => {
      refreshMetrics();
      if (canAnimate()) startAnimation();
      else render();
    };
    if ('ResizeObserver' in window) new ResizeObserver(refreshAndRender).observe(field);
    else window.addEventListener('resize', refreshAndRender, { passive: true });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) startAnimation();
        else stopAnimation();
      }, { rootMargin: '120px' }).observe(field);
    }
    const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => {
      reducedMotion = motionQuery.matches;
      if (reducedMotion) {
        stopAnimation();
        render();
      } else startAnimation();
    };
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', updateMotion);
    else motionQuery.addListener(updateMotion);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') startAnimation();
      else stopAnimation();
    });
    render();
    startAnimation();
  };

  // The reference page gives each mobile card rail a small, non-interactive
  // scroll indicator. Keep it in JavaScript so its position and page count
  // always reflect the cards that merchants have actually configured.
  const initSwipeCues = () => {
    const railSelector = [
      '.cal-grid--principles .cal-grid__cards',
      '.cal-grid--proof .cal-grid__cards',
      '.cal-grid--gallery .cal-grid__cards',
      '.cal-compare__grid'
    ].join(',');

    document.querySelectorAll(railSelector).forEach((rail) => {
      if (rail.dataset.calSwipeCueReady === 'true') return;
      const items = [...rail.children].filter((item) => item.classList.contains('cal-grid__card') || item.classList.contains('cal-compare__card'));
      if (items.length < 2) return;

      rail.dataset.calSwipeCueReady = 'true';
      const cue = document.createElement('div');
      cue.className = 'cal-swipe-cue';
      cue.setAttribute('aria-hidden', 'true');
      cue.innerHTML = '<span>Swipe</span><span class="cal-swipe-cue__track"><span class="cal-swipe-cue__thumb"></span></span><span data-cal-swipe-count></span>';
      rail.insertAdjacentElement('afterend', cue);

      const count = cue.querySelector('[data-cal-swipe-count]');
      let frame;
      const update = () => {
        frame = undefined;
        const scrollable = window.innerWidth <= 820 && rail.scrollWidth > rail.clientWidth + 4;
        cue.classList.toggle('is-scrollable', scrollable);
        if (!scrollable) return;

        const railRect = rail.getBoundingClientRect();
        const nearest = items.reduce((result, item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - railRect.left);
          return distance < result.distance ? { index, distance } : result;
        }, { index: 0, distance: Infinity });
        const total = items.length;
        const thumbSize = 100 / total;
        const progress = total > 1 ? nearest.index / (total - 1) : 0;

        cue.style.setProperty('--swipe-size', `${thumbSize}%`);
        cue.style.setProperty('--swipe-left', `${progress * (100 - thumbSize)}%`);
        if (count) count.textContent = `${String(nearest.index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      };
      const requestUpdate = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };

      rail.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(requestUpdate).observe(rail);
      requestUpdate();
    });
  };

  const init = () => {
    initHeroMedia();
    document.querySelectorAll('[data-cal-hero]').forEach(initHeroCarousel);
    document.querySelectorAll('[data-cal-hero]').forEach(initHeroCounters);
    document.querySelectorAll('[data-cal-story]').forEach(initStory);
    document.querySelectorAll('[data-cal-details]').forEach(initDetails);
    document.querySelectorAll('[data-cal-media-13]').forEach(initMedia13);
    initNavigation();
    initCompareSpecToggles();
    initCompareVariants();
    initCompareFastAdd();
    initSegmentedMedia();
    initDeferredMedia();
    initMobileMediaWarmup();
    initGalleryMedia();
    initDesignReplay();
    initAutoplayMedia();
    initCompactFaq();
    initSwipeCues();
    document.querySelectorAll('[data-cal-cta]').forEach(initCtaWordField);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
